import React, { useState } from "react";
import { Box, Button, Typography, useTheme } from "@mui/material";
import { Upload, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import MediaLibraryPickerCst from "@components/MediaLibraryPickerCst";
import { LearningResourceType, MediaType } from "src/interfaces/Media";
import { WizardStepProps } from "@components/WizardCst/types";
import { ActivityWizardData } from "../../../../types";

interface PackageContentProps extends WizardStepProps<ActivityWizardData> {
  locked?: boolean;
}

const PackageContent: React.FC<PackageContentProps> = ({
  data,
  setData,
  errors,
  locked = false,
}) => {
  const { t } = useTranslation("wizard-activity");
  const { t: tPicker } = useTranslation("media-library-picker");
  const theme = useTheme();

  const isXapi = data.type === "DA_XAPI";
  const fieldKey = isXapi ? "xapi_package_id" : "scorm_package_id";
  const learningResourceType = isXapi
    ? LearningResourceType.XAPI
    : LearningResourceType.SCORM;
  const current = data[fieldKey];
  const error = errors[fieldKey];

  const [pickerOpen, setPickerOpen] = useState(false);

  const [pickerAutoUpload, setPickerAutoUpload] = useState(false);

  const handlePickFromLibrary = () => {
    setPickerAutoUpload(false);
    setPickerOpen(true);
  };

  const handleUploadNew = () => {
    setPickerAutoUpload(true);
    setPickerOpen(true);
  };

  const handlePickerClose = () => {
    setPickerOpen(false);
    setPickerAutoUpload(false);
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    setData({ [fieldKey]: null } as Partial<ActivityWizardData>);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
          padding: "16px",
          borderRadius: "8px",
          border: `1px solid ${
            error ? theme.palette.error.main : theme.palette.divider
          }`,
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Button
          onClick={handlePickFromLibrary}
          variant="outlined"
          disabled={locked}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "8px",
            borderColor: theme.palette.divider,
            color: theme.palette.text.primary,
          }}
        >
          {t("content.package.pick-from-library")}
        </Button>
        <Button
          onClick={handleUploadNew}
          variant="text"
          startIcon={<Upload size={16} />}
          disabled={locked}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            color: theme.palette.primary.main,
          }}
        >
          {t("content.package.upload-new")}
        </Button>
      </Box>

      {current && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            padding: "10px 14px",
            borderRadius: "8px",
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
          }}
        >
          <Typography
            sx={{
              fontSize: "13px",
              color: theme.palette.text.primary,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {current.label || `#${current.id}`}
            <Box
              component="span"
              sx={{ color: theme.palette.text.tertiary, ml: 1 }}
            >
              {`| ID: ${current.id}`}
            </Box>
          </Typography>
          <Box
            onClick={locked ? undefined : handleRemove}
            sx={{
              cursor: locked ? "not-allowed" : "pointer",
              display: "flex",
              color: theme.palette.text.tertiary,
              opacity: locked ? 0.4 : 1,
              "&:hover": {
                color: locked
                  ? theme.palette.text.tertiary
                  : theme.palette.error.main,
              },
            }}
          >
            <X size={16} />
          </Box>
        </Box>
      )}

      {error && (
        <Typography
          sx={{
            fontSize: "12px",
            color: theme.palette.error.main,
            mt: 0.5,
            ml: 1,
          }}
        >
          {t(`errors.${error}`)}
        </Typography>
      )}

      <MediaLibraryPickerCst
        open={pickerOpen}
        onClose={handlePickerClose}
        title={
          isXapi
            ? tPicker("title-xapi") || "Aggiungi pacchetto xAPI"
            : tPicker("title-scorm") || "Aggiungi pacchetto SCORM"
        }
        mediaType={MediaType.LEARNING_RESOURCE}
        learningResourceType={learningResourceType}
        autoOpenUpload={pickerAutoUpload}
        multiple={false}
        initialSelection={
          current
            ? { id: Number(current.id), label: current.label || "" }
            : null
        }
        onConfirm={(selection) =>
          setData({ [fieldKey]: selection } as Partial<ActivityWizardData>)
        }
      />
    </Box>
  );
};

export default PackageContent;
